import type React from "react";
import { startTransition, useEffect, useState } from "react";

/**
 * Allows deferring the mounting of heavy components until after initial rendering.
 * Cons: the thread will still block once it finally mounts
 *
 * @example
 * ```
 * <DeferMount fallback={<Spinner/>}>
 * 	<HeavyComponent {...props}/>
 * </DeferMount>
 * ```
 */
export function DeferMount({
	children,
	strategy = "idle", // "idle" | "nextTick"
	fallback = null,
}: {
	children: React.ReactNode;
	strategy?: "idle" | "nextTick";
	fallback?: React.ReactNode;
}) {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (strategy === "idle" && "requestIdleCallback" in window) {
			const id = window.requestIdleCallback(() => {
				startTransition(() => setReady(true));
			});
			return () => window.cancelIdleCallback?.(id);
		}
		// next micro/macrotask
		const id = setTimeout(() => {
			startTransition(() => setReady(true));
		}, 0);
		return () => clearTimeout(id);
	}, [strategy]);

	return ready ? children : fallback;
}
